import React, { useEffect, useId, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { apiTokensService } from "../../services/apiTokens";
import OrbitalLoader from "../common/OrbitalLoader";
import useThemeColors from "../patterns/charts/useThemeColors";

const TIMEFRAMES = [
  { id: "1h", hours: 1, label: "1h" },
  { id: "1d", hours: 24, label: "1d" },
  { id: "1w", hours: 24 * 7, label: "1w" },
  { id: "30d", hours: 24 * 30, label: "30d" },
  { id: "1y", hours: 24 * 365, label: "1y" },
];

const STATUS_COLORS = ["#22c55e", "#3b82f6", "#f59e0b", "#ef4444", "#a855f7", "#14b8a6"];

const ChartTooltip = ({ active, payload, label, valueKey = "count", valueSuffix = "" }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const title = row.label || row.endpoint || row.name || row.status_code || label || "";
  const raw = payload[0]?.value ?? row[valueKey] ?? 0;
  const suffix = valueSuffix || (valueKey === "avg_ms" ? "ms" : ` request${raw === 1 ? "" : "s"}`);
  return (
    <div className="rounded-md border border-theme-secondary bg-theme-main px-3 py-2 shadow-lg text-xs text-theme-text pointer-events-none">
      <div className="font-medium max-w-[16rem] break-all">{String(title)}</div>
      <div className="opacity-70 mt-0.5">
        {raw}
        {suffix.startsWith(" ") ? suffix : ` ${suffix}`}
      </div>
    </div>
  );
};

function formatTick(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  if (String(value).includes("T")) {
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
}

function requestTypeKey(method, endpoint) {
  return `${method || ""}::${endpoint || ""}`;
}

/**
 * @param {"page"|"panel"} layout
 *   page = /api/usage/ (4 charts in one row)
 *   panel = account API settings (status + latency share a row)
 */
const ApiUsageCharts = ({ tokenHash = null, onSelectRequest, layout = "page", currentUser = null }) => {
  const colors = useThemeColors();
  const gradId = useId().replace(/:/g, "");
  const [timeframe, setTimeframe] = useState("30d");
  const [requestType, setRequestType] = useState("");
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const hours = TIMEFRAMES.find((t) => t.id === timeframe)?.hours || 24 * 30;
  const apiAllowed = currentUser?.limits?.api_requests_allowed;

  useEffect(() => {
    setLoading(true);
    const params = { hours };
    if (tokenHash) params.token_hash = tokenHash;
    if (requestType) {
      const [method, endpoint] = requestType.split("::");
      if (method) params.method = method;
      if (endpoint) params.endpoint = endpoint;
    }

    const loadSummary = tokenHash
      ? (ok, err) => apiTokensService.tokenUsage(tokenHash, params, ok, err)
      : (ok, err) => apiTokensService.usageSummary(params, ok, err);

    loadSummary(
      (res) => setSummary(res.data),
      () =>
        setSummary({
          total_requests: 0,
          avg_latency_ms: 0,
          timeseries: [],
          latency: [],
          endpoints: [],
          request_types: [],
          status_codes: [],
        }),
    );

    apiTokensService.listRequests(
      { ...params, page_size: 10 },
      (res) => {
        const rows = res.data?.results || res.data || [];
        setRecent(Array.isArray(rows) ? rows : []);
        setLoading(false);
      },
      () => {
        setRecent([]);
        setLoading(false);
      },
    );
  }, [tokenHash, hours, requestType]);

  if (loading && !summary) {
    return (
      <div className="flex justify-center py-16">
        <OrbitalLoader size={96} text="Loading usage..." />
      </div>
    );
  }

  const timeseries = summary?.timeseries || [];
  const latency = summary?.latency || [];
  const endpoints = (summary?.endpoints || []).map((row) => ({
    ...row,
    label: row.label || `${row.method || "GET"} ${row.endpoint || ""}`.trim(),
  }));
  const requestTypes = summary?.request_types || [];
  const statusCodes = (summary?.status_codes || []).map((row) => ({
    ...row,
    name: String(row.status_code),
  }));
  const timeframeLabel = TIMEFRAMES.find((t) => t.id === timeframe)?.label || "30d";
  const chartGridClass =
    layout === "page"
      ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4"
      : "grid grid-cols-1 xl:grid-cols-2 gap-4";

  const requestsChart = (
    <ChartCard title="Requests over time">
      {timeseries.length === 0 ? (
        <EmptyChart />
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={timeseries} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id={`apiRequestsFill-${gradId}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors.primary} stopOpacity={0.35} />
                <stop offset="100%" stopColor={colors.primary} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={colors.shadow} strokeOpacity={0.35} vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={formatTick}
              tick={{ fontSize: 10, fill: colors.text, fillOpacity: 0.5 }}
              stroke={colors.shadow}
              tickLine={false}
              axisLine={false}
              minTickGap={24}
            />
            <YAxis
              allowDecimals={false}
              width={28}
              tick={{ fontSize: 10, fill: colors.text, fillOpacity: 0.5 }}
              stroke={colors.shadow}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              content={<ChartTooltip />}
              allowEscapeViewBox={{ x: true, y: true }}
              wrapperStyle={{ outline: "none", zIndex: 20 }}
            />
            <Area
              type="monotone"
              dataKey="count"
              stroke={colors.primary}
              strokeWidth={2}
              fill={`url(#apiRequestsFill-${gradId})`}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );

  const endpointChart = (
    <ChartCard title="By endpoint">
      {endpoints.length === 0 ? (
        <EmptyChart />
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={endpoints.slice(0, 8)}
            layout="vertical"
            margin={{ top: 4, right: 8, bottom: 4, left: 4 }}
          >
            <CartesianGrid stroke={colors.shadow} strokeOpacity={0.35} horizontal={false} />
            <XAxis
              type="number"
              allowDecimals={false}
              tick={{ fontSize: 10, fill: colors.text, fillOpacity: 0.5 }}
              stroke={colors.shadow}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={layout === "page" ? 96 : 140}
              tick={{ fontSize: 9, fill: colors.text, fillOpacity: 0.7 }}
              stroke={colors.shadow}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => (value.length > (layout === "page" ? 16 : 26) ? `${value.slice(0, layout === "page" ? 14 : 24)}…` : value)}
            />
            <Tooltip
              content={<ChartTooltip />}
              cursor={{ fill: colors.shadow, fillOpacity: 0.25 }}
              allowEscapeViewBox={{ x: true, y: true }}
              wrapperStyle={{ outline: "none", zIndex: 20 }}
            />
            <Bar
              dataKey="count"
              fill={colors.primary}
              radius={[0, 4, 4, 0]}
              maxBarSize={22}
              cursor="pointer"
              onClick={(data) => {
                const row = data?.payload;
                if (!row?.method || !row?.endpoint) return;
                setRequestType(requestTypeKey(row.method, row.endpoint));
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );

  const statusChart = (
    <ChartCard title="Status codes">
      {statusCodes.length === 0 ? (
        <EmptyChart label="No requests yet." />
      ) : (
        <div className="flex items-center gap-3 h-full min-w-0">
          <div className="flex-1 min-w-0 h-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusCodes}
                  dataKey="count"
                  nameKey="name"
                  innerRadius="50%"
                  outerRadius="75%"
                  paddingAngle={2}
                  stroke={colors.mainBg}
                  strokeWidth={2}
                >
                  {statusCodes.map((row, index) => (
                    <Cell
                      key={row.status_code}
                      fill={STATUS_COLORS[index % STATUS_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  content={<ChartTooltip />}
                  allowEscapeViewBox={{ x: true, y: true }}
                  wrapperStyle={{ outline: "none", zIndex: 20 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="shrink-0 space-y-1.5 pr-1">
            {statusCodes.map((row, index) => (
              <div key={row.status_code} className="flex items-center gap-1.5 text-xs text-theme-text">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: STATUS_COLORS[index % STATUS_COLORS.length] }}
                />
                <span className="font-medium">{row.status_code}</span>
                <span className="opacity-60">{row.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </ChartCard>
  );

  const latencyChart = (
    <ChartCard title="Latency">
      {latency.length === 0 ? (
        <EmptyChart />
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={latency} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id={`apiLatencyFill-${gradId}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors.accent || colors.primary} stopOpacity={0.35} />
                <stop offset="100%" stopColor={colors.accent || colors.primary} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={colors.shadow} strokeOpacity={0.35} vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={formatTick}
              tick={{ fontSize: 10, fill: colors.text, fillOpacity: 0.5 }}
              stroke={colors.shadow}
              tickLine={false}
              axisLine={false}
              minTickGap={24}
            />
            <YAxis
              allowDecimals={false}
              width={36}
              tick={{ fontSize: 10, fill: colors.text, fillOpacity: 0.5 }}
              stroke={colors.shadow}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `${v}`}
            />
            <Tooltip
              content={<ChartTooltip valueKey="avg_ms" valueSuffix="ms avg" />}
              allowEscapeViewBox={{ x: true, y: true }}
              wrapperStyle={{ outline: "none", zIndex: 20 }}
            />
            <Area
              type="monotone"
              dataKey="avg_ms"
              stroke={colors.accent || colors.primary}
              strokeWidth={2}
              fill={`url(#apiLatencyFill-${gradId})`}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );

  return (
    <div className={`space-y-6 ${loading ? "opacity-70" : ""}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-semibold text-theme-text">Usage</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={requestType}
            onChange={(e) => setRequestType(e.target.value)}
            className="max-w-[16rem] px-2.5 py-1.5 text-xs rounded-md border border-theme-secondary bg-theme-main text-theme-text"
          >
            <option value="">All request types</option>
            {requestTypes.map((row) => {
              const key = requestTypeKey(row.method, row.endpoint);
              return (
                <option key={key} value={key}>
                  {row.label || `${row.method} ${row.endpoint}`}
                </option>
              );
            })}
          </select>
          <div className="flex shrink-0 rounded-lg border border-theme-secondary p-0.5">
            {TIMEFRAMES.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setTimeframe(option.id)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                  timeframe === option.id
                    ? "bg-theme-primary text-theme-button"
                    : "text-theme-text opacity-70 hover:opacity-100"
                }`}
                style={
                  timeframe === option.id
                    ? { backgroundColor: "var(--theme-primary)", color: "var(--theme-button-text)" }
                    : {}
                }
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <div className="rounded-lg border border-theme-secondary bg-theme-secondary bg-opacity-30 p-4">
          <div className="text-xs text-theme-text opacity-60">
            API Requests ({timeframeLabel})
          </div>
          <div className="text-2xl font-semibold text-theme-text mt-1">
            {summary?.total_requests ?? 0}
            {timeframe === "30d" && apiAllowed != null && (
              <span className="text-sm font-medium opacity-60 ml-1">/ {apiAllowed}</span>
            )}
          </div>
        </div>
        <div className="rounded-lg border border-theme-secondary bg-theme-secondary bg-opacity-30 p-4">
          <div className="text-xs text-theme-text opacity-60">Success rate</div>
          <div className="text-2xl font-semibold text-theme-text mt-1">
            {summary?.success_rate ?? 0}
            <span className="text-sm font-medium opacity-60 ml-1">%</span>
          </div>
        </div>
        <div className="rounded-lg border border-theme-secondary bg-theme-secondary bg-opacity-30 p-4">
          <div className="text-xs text-theme-text opacity-60">p50 latency</div>
          <div className="text-2xl font-semibold text-theme-text mt-1">
            {summary?.p50_ms ?? 0}
            <span className="text-sm font-medium opacity-60 ml-1">ms</span>
          </div>
        </div>
        <div className="rounded-lg border border-theme-secondary bg-theme-secondary bg-opacity-30 p-4">
          <div className="text-xs text-theme-text opacity-60">p75 latency</div>
          <div className="text-2xl font-semibold text-theme-text mt-1">
            {summary?.p75_ms ?? 0}
            <span className="text-sm font-medium opacity-60 ml-1">ms</span>
          </div>
        </div>
        <div className="rounded-lg border border-theme-secondary bg-theme-secondary bg-opacity-30 p-4">
          <div className="text-xs text-theme-text opacity-60">p95 latency</div>
          <div className="text-2xl font-semibold text-theme-text mt-1">
            {summary?.p95_ms ?? 0}
            <span className="text-sm font-medium opacity-60 ml-1">ms</span>
          </div>
        </div>
      </div>

      {layout === "page" ? (
        <div className={chartGridClass}>
          {requestsChart}
          {endpointChart}
          {statusChart}
          {latencyChart}
        </div>
      ) : (
        <>
          <div className={chartGridClass}>
            {requestsChart}
            {endpointChart}
          </div>
          <div className={chartGridClass}>
            {statusChart}
            {latencyChart}
          </div>
        </>
      )}

      <div>
        <h3 className="text-sm font-semibold text-theme-text mb-3">Recent requests</h3>
        <div className="border border-theme-secondary rounded-lg divide-y divide-theme-secondary overflow-hidden">
          {recent.length === 0 && (
            <p className="p-4 text-sm text-theme-text opacity-60">No recent requests.</p>
          )}
          {recent.map((row) => (
            <button
              key={row.hash}
              type="button"
              onClick={() => onSelectRequest?.(row)}
              className="w-full text-left px-4 py-3 hover:bg-black hover:bg-opacity-5 transition-colors"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-mono text-theme-text truncate">
                    {row.method} {row.endpoint}
                  </div>
                  <div className="text-xs text-theme-text opacity-60 mt-0.5">
                    {new Date(row.create_date).toLocaleString()} · {row.milliseconds}ms
                  </div>
                </div>
                <span className="text-sm font-semibold text-theme-text">{row.status_code}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

function ChartCard({ title, children }) {
  return (
    <div className="rounded-lg border border-theme-secondary p-4 overflow-hidden min-w-0">
      <h3 className="text-sm font-semibold text-theme-text mb-3">{title}</h3>
      <div className="h-52">{children}</div>
    </div>
  );
}

function EmptyChart({ label = "No data for this timeframe." }) {
  return (
    <div className="h-full flex items-center justify-center text-sm text-theme-text opacity-50">
      {label}
    </div>
  );
}

export default ApiUsageCharts;
